import React, { useRef, useState, useCallback, useEffect } from 'react';
import type { TeamComponent } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { Linkedin, Twitter, Mail, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import profilePlaceholder from '../assets/profile-placeholder.png';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';
import { getFontFamily } from '../utils/fontLoader';

interface TeamComponentProps {
  component: TeamComponent;
}

export const TeamComponentRenderer: React.FC<TeamComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Normalize data to handle missing fields from Roary
  const rawData = component.data as Record<string, unknown>;
  const rawMembers = (rawData.members || []) as Array<Record<string, unknown>>;

  // Normalize each member to ensure required fields exist
  const normalizedMembers = rawMembers.map((member, index) => ({
    ...member,
    id: (member.id as string) || `team-member-${index}`,
    name: member.name as string | undefined,
    position: member.position as string | undefined,
    bio: member.bio as string | undefined,
    imageUrl: member.imageUrl as string | undefined,
    order: (member.order as number) ?? index,
  }));

  const data = {
    ...component.data,
    members: normalizedMembers,
    layout: rawData.layout || 'grid',
    columns: String(rawData.columns || '3') as '1' | '2' | '3' | '4' | '5' | '6',
    showPosition: rawData.showPosition !== false,
    showBio: rawData.showBio !== false,
    showSocialLinks: rawData.showSocialLinks !== false,
    imageShape: rawData.imageShape || 'circle',
  } as typeof component.data;

  const {
    title,
    subtitle,
    description,
    members,
    layout,
    columns,
    alignment,
    cardStyle,
    showPhoto,
    showPosition,
    showBio,
    showSocialLinks,
    imageShape,
    imageSize,
    backgroundColor,
    textColor,
    cardBackgroundColor,
    cardTextColor,
    accentColor,
    spacing,
    cardShadow,
    cardBorder,
    hoverEffect,
    animationDelay,
  } = data;

  // Sort members by order
  const sortedMembers = [...members].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Grid column classes
  const getGridColumns = () => {
    const colMap = {
      '1': 'grid-cols-1',
      '2': 'grid-cols-1 md:grid-cols-2',
      '3': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      '4': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      // Avoid md:grid-cols-3 on small viewports — it matched "mobile" widths and caused overflow.
      '5': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
      '6': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
    };
    return colMap[columns] || colMap['3'];
  };

  // Spacing classes
  const getSpacing = () => {
    const spacingMap = {
      compact: 'gap-4',
      normal: 'gap-6',
      spacious: 'gap-8',
    };
    return spacingMap[spacing] || spacingMap.normal;
  };

  // Image size classes
  const getImageSize = () => {
    const sizeMap = {
      small: 'w-16 h-16',
      medium: 'w-24 h-24',
      large: 'w-32 h-32',
    };
    return sizeMap[imageSize] || sizeMap.medium;
  };

  // Image shape classes
  const getImageShape = () => {
    const shapeMap = {
      square: 'rounded-none',
      circle: 'rounded-full',
      'rounded-square': 'rounded-lg',
    };
    return shapeMap[imageShape] || shapeMap.circle;
  };

  // Card style classes
  const getCardStyle = () => {
    const baseClasses = 'p-6 transition-all duration-300';
    const shadowClass = cardShadow ? 'shadow-lg' : '';
    const borderClass = cardBorder ? `border border-gray-200` : '';

    const styleMap = {
      default: `${baseClasses} ${shadowClass} ${borderClass}`,
      minimal: `${baseClasses} ${borderClass}`,
      'hover-effect': `${baseClasses} ${shadowClass} ${borderClass} hover:shadow-xl`,
      bordered: `${baseClasses} border-2 ${borderClass}`,
      rounded: `${baseClasses} rounded-xl ${shadowClass} ${borderClass}`,
    };

    return styleMap[cardStyle] || styleMap.default;
  };

  // Hover effect classes
  const getHoverEffect = () => {
    const effectMap = {
      none: '',
      lift: 'hover:-translate-y-2',
      zoom: 'hover:scale-105',
      fade: 'hover:opacity-90',
    };
    return effectMap[hoverEffect] || effectMap.none;
  };

  // Alignment classes
  const getAlignment = () => {
    const alignMap = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    };
    return alignMap[alignment] || alignMap.center;
  };

  // Social link icon component
  const SocialIcon = ({ type, url }: { type: string; url: string }) => {
    const icons = {
      linkedin: Linkedin,
      twitter: Twitter,
      email: Mail,
      website: Globe,
    };

    const Icon = icons[type as keyof typeof icons];
    if (!Icon) return null;

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (type === 'email') {
        window.location.href = `mailto:${url}`;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    // Color priority: component.customColors > deprecated fields > theme colors
    const iconColor =
      data.customColors?.roleColor ||
      accentColor ||
      themeStyles.team?.roleColor ||
      themeStyles.features?.iconColor;

    return (
      <button
        onClick={handleClick}
        className={`p-2 rounded-full transition-colors duration-200 ${currentTheme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
        style={{ color: iconColor }}
        aria-label={`${type} link`}
      >
        <Icon size={18} />
      </button>
    );
  };

  // Team member card component
  const TeamMemberCard = ({
    member,
    index,
  }: {
    member: TeamComponent['data']['members'][0];
    index: number;
  }) => {
    const animationClass = animationDelay === 'stagger' ? `animate-fade-in-up` : '';
    const animationDelay_ms =
      animationDelay === 'stagger' ? { animationDelay: `${index * 100}ms` } : {};

    // Color priority: component.customColors > deprecated fields > theme colors
    const cardBg =
      data.customColors?.cardBackground || cardBackgroundColor || themeStyles.team?.cardBg;
    const cardText = data.customColors?.cardText || cardTextColor || themeStyles.team?.nameColor;
    const nameColor = data.customColors?.nameColor || themeStyles.team?.nameColor;
    const roleColor = data.customColors?.roleColor || accentColor || themeStyles.team?.roleColor;

    return (
      <div
        className={`${getCardStyle()} ${getHoverEffect()} ${animationClass}`}
        style={{
          backgroundColor: cardBg,
          color: cardText,
          ...animationDelay_ms,
        }}
      >
        <div
          className={`flex ${layout === 'list' ? 'flex-row space-x-4' : 'flex-col items-center space-y-4'}`}
        >
          {/* Member Image - Show if showPhoto is enabled */}
          {showPhoto !== false && (
            <div className="flex-shrink-0">
              {(() => {
                const memberCrop = (member as any).crop;
                const hasEffects = hasCropApplied(memberCrop);
                const cropStyles = getCroppedImageStyles(memberCrop);

                if (hasEffects) {
                  return (
                    <div
                      className={`${getImageSize()} ${getImageShape()} relative overflow-hidden`}
                    >
                      <img
                        src={member.cloudfrontUrl || member.imageUrl || profilePlaceholder}
                        alt={
                          member.imageAlt ||
                          (member.name ? `${member.name} profile photo` : 'Team member photo')
                        }
                        style={cropStyles}
                      />
                    </div>
                  );
                }

                return (
                  <img
                    src={member.cloudfrontUrl || member.imageUrl || profilePlaceholder}
                    alt={
                      member.imageAlt ||
                      (member.name ? `${member.name} profile photo` : 'Team member photo')
                    }
                    className={`${getImageSize()} ${getImageShape()} object-cover`}
                  />
                );
              })()}
            </div>
          )}

          {/* Member Info */}
          <div className={`flex-1 ${layout === 'list' ? 'text-left' : getAlignment()}`}>
            {/* Name - Only show if provided */}
            {member.name && (
              <h3
                className="text-lg font-semibold mb-1"
                style={{ color: nameColor || cardText, ...headingFontStyle }}
              >
                {member.name}
              </h3>
            )}

            {/* Position - Only show if provided and showPosition is enabled */}
            {showPosition && member.position && (
              <p
                className="text-sm font-medium mb-2"
                style={{ color: roleColor, ...bodyFontStyle }}
              >
                {member.position}
              </p>
            )}

            {/* Bio - Adjust spacing if no name/position */}
            {showBio && member.bio && (
              <p
                className={`text-sm opacity-80 ${member.name || member.position ? 'mb-4' : 'mb-2'}`}
                style={{ color: cardText, ...bodyFontStyle }}
              >
                {member.bio}
              </p>
            )}

            {/* Social Links - Center them if no text content above */}
            {showSocialLinks && member.socialLinks && (
              <div
                className={`flex space-x-2 ${!member.name && !member.position ? 'justify-center' : layout === 'list' ? 'justify-start' : 'justify-center'}`}
              >
                {member.socialLinks.linkedin && (
                  <SocialIcon type="linkedin" url={member.socialLinks.linkedin} />
                )}
                {member.socialLinks.twitter && (
                  <SocialIcon type="twitter" url={member.socialLinks.twitter} />
                )}
                {member.socialLinks.email && (
                  <SocialIcon type="email" url={member.socialLinks.email} />
                )}
                {member.socialLinks.website && (
                  <SocialIcon type="website" url={member.socialLinks.website} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Color priority: component.customColors > deprecated fields > theme colors
  const sectionBgColor =
    data.customColors?.background || backgroundColor || themeStyles.team?.background;
  const sectionTextColor = data.customColors?.text || textColor || themeStyles.team?.nameColor;

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.team?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.team?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    if (!data.buttons || data.buttons.length === 0) return null;

    return (
      <div
        className={`flex flex-wrap gap-4 mt-12 ${alignment === 'center' ? 'justify-center' : alignment === 'right' ? 'justify-end' : 'justify-start'}`}
      >
        {data.buttons.map((button, index) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  // --- Carousel logic ---
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);

    // Derive active index from scroll position
    const cardWidth = el.querySelector('[data-carousel-card]')?.clientWidth || 320;
    const gap = 24;
    const idx = Math.round(el.scrollLeft / (cardWidth + gap));
    setActiveIndex(Math.min(idx, sortedMembers.length - 1));
  }, [sortedMembers.length]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el || layout !== 'carousel') return;

    updateScrollState();

    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);

    return () => observer.disconnect();
  }, [layout, updateScrollState]);

  const scrollToIndex = useCallback((index: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelectorAll('[data-carousel-card]')[index] as HTMLElement | undefined;
    if (card) {
      el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' });
    }
  }, []);

  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-carousel-card]')?.clientWidth || 320;
    const gap = 24;
    const scrollAmount = cardWidth + gap;
    const newScroll =
      direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;
    el.scrollTo({ left: newScroll, behavior: 'smooth' });
  }, []);

  // Arrow button color derived from theme
  const arrowColor =
    data.customColors?.roleColor ||
    accentColor ||
    themeStyles.team?.roleColor ||
    themeStyles.features?.iconColor ||
    '#6b7280';

  // Number of cards visible at once, driven by the columns setting
  const carouselCols = Math.max(1, Math.min(6, parseInt(columns) || 3));

  return (
    <section
      className="py-16 md:py-24"
      style={{
        backgroundColor: sectionBgColor,
        color: sectionTextColor,
      }}
    >
      <div className="max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {(title || subtitle || description) && (
          <div className={`mb-12 ${getAlignment()}`}>
            {title && (
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: sectionTextColor, ...headingFontStyle }}
              >
                {title}
              </h2>
            )}

            {subtitle && (
              <p
                className="text-xl mb-6 opacity-80"
                style={{ color: sectionTextColor, ...bodyFontStyle }}
              >
                {subtitle}
              </p>
            )}

            {description && (
              <p
                className="opacity-80"
                style={{
                  color: sectionTextColor,
                  textAlign: alignment || 'center',
                  ...bodyFontStyle,
                }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        {/* Team Members */}
        {layout === 'grid' && (
          <div className={`grid ${getGridColumns()} ${getSpacing()} [&>*]:min-w-0`}>
            {sortedMembers.map((member, index) => (
              <TeamMemberCard key={member.id || index} member={member} index={index} />
            ))}
          </div>
        )}

        {layout === 'list' && (
          <div className={`space-y-6`}>
            {sortedMembers.map((member, index) => (
              <TeamMemberCard key={member.id || index} member={member} index={index} />
            ))}
          </div>
        )}

        {layout === 'carousel' && (
          <div className="relative">
            {/* Left arrow -- always visible when scrollable */}
            {canScrollLeft && (
              <button
                onClick={() => scrollCarousel('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110 -ml-5"
                style={{ color: arrowColor, backgroundColor: 'rgba(255,255,255,0.9)' }}
                aria-label="Previous"
              >
                <ChevronLeft size={22} />
              </button>
            )}

            {/* Right arrow -- always visible when scrollable */}
            {canScrollRight && (
              <button
                onClick={() => scrollCarousel('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110 -mr-5"
                style={{ color: arrowColor, backgroundColor: 'rgba(255,255,255,0.9)' }}
                aria-label="Next"
              >
                <ChevronRight size={22} />
              </button>
            )}

            {/* Edge fade gradients */}
            {canScrollLeft && (
              <div
                className="absolute left-0 top-0 bottom-0 w-12 z-[5] pointer-events-none"
                style={{
                  background: `linear-gradient(to right, ${sectionBgColor || '#ffffff'}ee, transparent)`,
                }}
              />
            )}
            {canScrollRight && (
              <div
                className="absolute right-0 top-0 bottom-0 w-12 z-[5] pointer-events-none"
                style={{
                  background: `linear-gradient(to left, ${sectionBgColor || '#ffffff'}ee, transparent)`,
                }}
              />
            )}

            {/* Scrollable track with snap */}
            <div
              ref={carouselRef}
              onScroll={updateScrollState}
              className="flex gap-6 overflow-x-auto scroll-smooth pb-2"
              style={{
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {sortedMembers.map((member, index) => (
                <div
                  key={member.id || index}
                  data-carousel-card
                  className="flex-shrink-0"
                  style={{
                    scrollSnapAlign: 'start',
                    width: `calc((100% - ${(carouselCols - 1) * 1.5}rem) / ${carouselCols})`,
                    minWidth: 'min(250px, 80vw)',
                  }}
                >
                  <TeamMemberCard member={member} index={index} />
                </div>
              ))}
            </div>

            {/* Dot indicators */}
            {sortedMembers.length > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {sortedMembers.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToIndex(index)}
                    aria-label={`Go to slide ${index + 1}`}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: activeIndex === index ? 24 : 8,
                      height: 8,
                      backgroundColor:
                        activeIndex === index
                          ? arrowColor
                          : currentTheme === 'dark'
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.15)',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {layout === 'masonry' && (
          <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6">
            {sortedMembers.map((member, index) => (
              <div key={member.id || index} className="break-inside-avoid mb-6">
                <TeamMemberCard member={member} index={index} />
              </div>
            ))}
          </div>
        )}

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>

      {/* Add CSS animation styles */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }

        /* Hide scrollbar for carousel */
        [data-carousel-card]:first-child {
          scroll-margin-left: 0;
        }
        div:has(> [data-carousel-card])::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};
